import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/feature/EmptyState";
import FilterBar from "@/pages/category/components/FilterBar";
import Pagination from "@/pages/category/components/Pagination";
import { movies } from "@/mocks/movies";
import { useSeo } from "@/hooks/useSeo";
import type { Movie } from "@/types/movie";

const PAGE_SIZE = 12;
const MAX_PAGES = 8;

const meta: Record<string, { slogan: string; desc: string; keywords: string }> = {
  电视剧: {
    slogan: "多彩爱情故事，陪伴你的每个夜晚",
    desc: "都市情感、青春校园、古装情缘与海外佳剧，这里收录了丰富的电视剧作品，满足您对情感爱情影视的所有期待。",
    keywords: "爱情电视剧,情感电视剧,都市情感剧,古装情缘剧,青春校园剧,恋爱的味道",
  },
  电影: {
    slogan: "一帧光影，一段心动",
    desc: "这里收录了丰富的爱情电影作品，从经典到热映，满足您对情感爱情影视的所有期待。",
    keywords: "爱情电影,情感电影,经典爱情片,电影推荐,剧情简介,恋爱的味道",
  },
};

export default function CategoryPage() {
  const navigate = useNavigate();
  const { categoryName } = useParams();
  const category = decodeURIComponent(categoryName || "");
  const info = meta[category];

  const [sub, setSub] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState("最新");
  const [page, setPage] = useState(1);

  const baseList: Movie[] = useMemo(
    () => (info ? movies.filter((m) => m.type === category) : []),
    [category, info],
  );

  const subOptions = useMemo(
    () => Array.from(new Set(baseList.map((m) => m.category))),
    [baseList],
  );

  const filtered = useMemo(() => {
    let list = [...baseList];
    if (sub !== "全部") list = list.filter((m) => m.category === sub);
    if (region !== "全部") list = list.filter((m) => m.region === region);
    if (year === "近一年") list = list.filter((m) => m.year >= 2023);
    if (year === "近三年") list = list.filter((m) => m.year >= 2021);
    if (year === "经典") list = list.filter((m) => m.year <= 2010);

    if (sort === "最新") list.sort((a, b) => b.year - a.year || b.views - a.views);
    if (sort === "热门") list.sort((a, b) => b.views - a.views);
    if (sort === "评分") list.sort((a, b) => b.rating - a.rating);

    return list;
  }, [baseList, sub, region, year, sort]);

  const totalPages = Math.min(Math.ceil(filtered.length / PAGE_SIZE), MAX_PAGES);
  const safePage = Math.min(page, Math.max(totalPages, 1));
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  useSeo({
    title: info
      ? `${category} - 恋爱的味道情感影视推荐`
      : "分类未找到 - 恋爱的味道",
    description: info
      ? `这里收录了丰富的${category}影视作品，满足您对情感爱情影视的所有期待。`
      : "抱歉，未找到对应的影视分类，请返回首页浏览其他内容。",
    keywords: info?.keywords,
  });

  const reset = () => {
    setSub("全部");
    setRegion("全部");
    setYear("全部");
    setSort("最新");
    setPage(1);
  };

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8">
        <nav className="flex flex-wrap items-center gap-1 text-sm text-foreground-600">
          <Link to="/" className="transition-colors hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span>分类</span>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900">{info ? category : "未知分类"}</span>
        </nav>

        {!info ? (
          <div className="mt-8">
            <EmptyState
              title="未找到该分类"
              desc="当前分类不存在，可能链接有误。您可以返回首页继续浏览情感影视作品。"
              actionLabel="返回首页"
              onAction={() => navigate("/")}
            />
          </div>
        ) : (
          <>
            <div className="mt-6 flex flex-col gap-2 border-b border-background-200 pb-6">
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                {category} · {info.slogan}
              </h1>
              <p className="max-w-3xl text-sm leading-relaxed text-foreground-600">{info.desc}</p>
            </div>

            <div className="mt-6">
              <FilterBar
                subOptions={subOptions}
                sub={sub}
                region={region}
                year={year}
                sort={sort}
                onSub={(v) => {
                  setSub(v);
                  setPage(1);
                }}
                onRegion={(v) => {
                  setRegion(v);
                  setPage(1);
                }}
                onYear={(v) => {
                  setYear(v);
                  setPage(1);
                }}
                onSort={(v) => {
                  setSort(v);
                  setPage(1);
                }}
              />
            </div>

            <p className="mt-6 text-sm text-foreground-600">
              共找到 <strong className="font-semibold text-primary-600">{filtered.length}</strong> 部相关作品
            </p>

            {pageItems.length === 0 ? (
              <div className="mt-6">
                <EmptyState
                  title="暂无符合条件的影视作品"
                  desc="暂无符合条件的影视作品，请调整筛选条件后重试。"
                  actionLabel="重置筛选条件"
                  onAction={reset}
                />
              </div>
            ) : (
              <>
                <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                  {pageItems.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
                <Pagination current={safePage} total={totalPages} onChange={setPage} />
              </>
            )}
          </>
        )}
      </div>
    </SiteLayout>
  );
}